import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  InputAdornment,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Divider,
  Alert,
  Snackbar,
  MenuItem,
  CircularProgress,
  Grid,
  Checkbox,
} from '@mui/material';
import {
  Share as ShareIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  LockOutlined as LockIcon,
  EventOutlined as EventIcon,
  TimerOutlined as TimeIcon,
  LinkOff as LinkOffIcon,
  PersonRemove as PersonRemoveIcon,
  ManageAccounts as ManageAccountsIcon,
} from '@mui/icons-material';
import { 
  createFileShare, 
  createFolderShare, 
  removeShare,
  shareWithUsers,
} from '../../store/slices/shareSlice';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { format, addDays, differenceInDays } from 'date-fns';
import { SetPasswordDialog } from './PasswordProtection';
import api from '../../utils/api';
import { useSnackbar } from 'notistack';
import {
  ShareType,
  ResourceType,
  ShareAccessMode,
  PublicAccessType,
} from '../../types/shares';
import ShareAnalytics from './ShareAnalytics';

const SHARE_COLLABORATOR_HINT =
  "Can't find someone? They need to sign in to Drive at least once. After that they'll appear in this list and you can share with them.";

type CollaboratorOption = { id: string; name: string; email: string };

type ResourceShareLink = {
  id: string;
  kind: 'link';
  shareType: ShareType;
  accessMode?: ShareAccessMode;
  publicAccessType?: PublicAccessType;
  hasPassword: boolean;
  expiresAt?: string | null;
  accessCount: number;
  createdAt: string;
};

type ResourceUserShare = {
  id: string;
  kind: 'user';
  inherited?: boolean;
  removable?: boolean;
  managedByAccessOps?: boolean;
  shareType: ShareType;
  userId: string;
  userName: string;
  userEmail: string;
  createdAt: string;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`share-tabpanel-${index}`}
      aria-labelledby={`share-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemName: string;
  itemType: ResourceType;
  currentShares?: any[];
}

interface SetPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onSetPassword: (password: string) => Promise<void>;
  isReset: boolean;
}

const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  onClose,
  itemId,
  itemName,
  itemType,
  currentShares = [],
}) => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [shareLink, setShareLink] = useState<string>('');
  const [shareType, setShareType] = useState<ShareType>('view');
  const [expirationEnabled, setExpirationEnabled] = useState(false);
  const [expirationDate, setExpirationDate] = useState<Date | null>(addDays(new Date(), 7));
  const [accessMode, setAccessMode] = useState<ShareAccessMode>('public');
  const [publicAccessType, setPublicAccessType] = useState<PublicAccessType>('direct');
  const [maxViews, setMaxViews] = useState<number>(10);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkPasswordConfirm, setLinkPasswordConfirm] = useState('');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [createdAccessSummary, setCreatedAccessSummary] = useState<{
    accessMode: ShareAccessMode;
    publicAccessType?: PublicAccessType;
    maxViews?: number | null;
    hasPassword?: boolean;
  } | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [linkCreated, setLinkCreated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [users, setUsers] = useState<CollaboratorOption[]>([]);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [sharesError, setSharesError] = useState<string | null>(null);
  const [nextUserOffset, setNextUserOffset] = useState<number | null>(null);
  const userRequest = useRef(0);
  const shareRequest = useRef(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [shareLinksList, setShareLinksList] = useState<ResourceShareLink[]>([]);
  const [userSharesList, setUserSharesList] = useState<ResourceUserShare[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [selectedShareId, setSelectedShareId] = useState<string | null>(null);
  const [expirationTime, setExpirationTime] = useState<Date | null>(
    new Date(new Date().setHours(23, 59, 59, 999))
  );

  const fetchResourceShares = async (signal?: AbortSignal) => {
    const generation = ++shareRequest.current;
    setSharesError(null);
    try {
      setLoadingShares(true);
      const response = await api.get(`/shares/resource/${itemType}/${itemId}`, {signal});
      if (signal?.aborted || generation !== shareRequest.current) return;
      const links: ResourceShareLink[] = response.data.shareLinks || [];
      const users: ResourceUserShare[] = response.data.userShares || [];
      setShareLinksList(links);
      setUserSharesList(users);
      if (links.length > 0 && !selectedShareId) {
        setSelectedShareId(links[0].id);
      }
    } catch (error: any) {
      if (!signal?.aborted && generation === shareRequest.current) setSharesError(error.response?.data?.message || 'Unable to load sharing details');
    } finally {
      if (generation === shareRequest.current) setLoadingShares(false);
    }
  };

  useEffect(() => {
    setShareLinksList([]); setUserSharesList([]); setSelectedShareId(null);
    if (!open) return;
    const controller = new AbortController();
    void fetchResourceShares(controller.signal);
    setSelectedUserIds([]); setUserSearchQuery(''); setError(null);
    return () => {controller.abort(); shareRequest.current++;};
  }, [open, itemId, itemType]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = setTimeout(() => void fetchUsers(userSearchQuery.trim(), 0, controller.signal), 250);
    return () => {clearTimeout(timer); controller.abort(); userRequest.current++;};
  }, [open, userSearchQuery]);

  const filteredUsers = users.filter((u) => {
    const q = userSearchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  });

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const fetchUsers = async (query = '', offset = 0, signal?: AbortSignal) => {
    const generation = ++userRequest.current; setUsersError(null);
    try {
      setLoadingUsers(true);
      const response = await api.get('/users/share-candidates', {params: {query, offset, limit: 25}, signal});
      if (signal?.aborted || generation !== userRequest.current) return;
      const list: CollaboratorOption[] = (response.data.users || []).map(
        (u: { id: string; email: string; name?: string; firstName?: string; lastName?: string }) => ({
          id: u.id,
          email: u.email,
          name:
            u.name ||
            `${u.firstName || ''} ${u.lastName || ''}`.trim() ||
            u.email,
        })
      );
      setUsers(previous => offset ? [...previous, ...list] : list);
      setNextUserOffset(response.data.nextOffset ?? null);
    } catch {
      if (!signal?.aborted && generation === userRequest.current) setUsersError('Unable to load people. Please retry.');
    } finally {
      if (generation === userRequest.current) setLoadingUsers(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // If switching to analytics tab and there's a link share, select it
    if (newValue === 3 && shareLinksList.length > 0 && !selectedShareId) {
      setSelectedShareId(shareLinksList[0].id);
    }
  };

  const formatLinkShareLabel = (link: ResourceShareLink) => {
    const visibility = link.accessMode === 'private' ? 'Organization only' : 'Public link';
    const parts = [
      visibility,
      link.shareType,
      new Date(link.createdAt).toLocaleDateString(),
    ];
    if (link.hasPassword) parts.push('password protected');
    if (link.expiresAt) {
      parts.push(`expires ${new Date(link.expiresAt).toLocaleDateString()}`);
    }
    return parts.join(' · ');
  };

  const handleRevokeLink = async (shareId: string) => {
    try {
      setRevokingId(shareId);
      const resultAction = await dispatch(removeShare(shareId) as any);
      if (resultAction.meta.requestStatus === 'fulfilled') {
        enqueueSnackbar('Share link revoked', { variant: 'success' });
        if (selectedShareId === shareId) {
          setSelectedShareId(null);
        }
        await fetchResourceShares();
      } else {
        enqueueSnackbar('Failed to revoke share link', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Failed to revoke share link', { variant: 'error' });
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevokeUserAccess = async (permissionId: string) => {
    try {
      setRevokingId(permissionId);
      await api.delete(`/shares/permission/${permissionId}`);
      enqueueSnackbar('User access removed', { variant: 'success' });
      await fetchResourceShares();
    } catch {
      enqueueSnackbar('Failed to remove user access', { variant: 'error' });
    } finally {
      setRevokingId(null);
    }
  };

  // Create shareable link
  const handleCreateLink = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let expirationTimestamp: string | null = null;
      let expirationDays = null;
      let expirationTimeString = null;
      
      if (expirationEnabled && expirationDate) {
        // Combine date and time into a single datetime
        const expirationDateTime = new Date(expirationDate);
        if (expirationTime) {
          expirationDateTime.setHours(expirationTime.getHours());
          expirationDateTime.setMinutes(expirationTime.getMinutes());
          expirationDateTime.setSeconds(0);
          expirationDateTime.setMilliseconds(0);
        } else {
          // Default to end of day if no time specified
          expirationDateTime.setHours(23, 59, 59, 999);
        }
        
        // Check if expiration is in the future
        const now = new Date();
        if (expirationDateTime <= now) {
          setError('Expiration date and time must be in the future');
          enqueueSnackbar('Expiration date and time must be in the future', { variant: 'error' });
          setLoading(false);
          return;
        }
        
        // Calculate days difference (can be 0 for same day)
        const diff = differenceInDays(expirationDateTime, now);
        expirationDays = diff >= 0 ? diff : 0;
        
        expirationTimestamp = expirationDateTime.toISOString();
        // Format time as HH:MM
        if (expirationTime) {
          const hours = expirationTime.getHours().toString().padStart(2, '0');
          const minutes = expirationTime.getMinutes().toString().padStart(2, '0');
          expirationTimeString = `${hours}:${minutes}`;
        } else {
          expirationTimeString = '23:59';
        }
      }
      
      // API endpoint based on item type (match server's '/api/shares' path)
      const endpoint = itemType === 'file' 
        ? `/shares/file/${itemId}` 
        : `/shares/folder/${itemId}`;
      
      const needsPassword =
        accessMode === 'public' &&
        (publicAccessType === 'password' || publicAccessType === 'password_limited');
      const needsLimit =
        accessMode === 'public' &&
        (publicAccessType === 'limited_views' || publicAccessType === 'password_limited');

      if (needsPassword) {
        if (linkPassword.length < 8) {
          setError('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        if (linkPassword !== linkPasswordConfirm) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
      }

      if (needsLimit && (!maxViews || maxViews < 1)) {
        setError('Enter a maximum view count of at least 1');
        setLoading(false);
        return;
      }

      const response = await api.post(endpoint, {
        shareType: 'view',
        expiration: expirationEnabled ? expirationTimestamp : null,
        accessMode,
        publicAccessType: accessMode === 'public' ? publicAccessType : undefined,
        maxViews: needsLimit ? maxViews : undefined,
        password: needsPassword ? linkPassword : null
      });
      
      if (response.data.shareLink) {
        const { token, id: shareId } = response.data.shareLink;
        const shareUrl = `${window.location.origin}/share/${token}`;
        setShareLink(shareUrl);
        setLinkCreated(true);
        setSelectedShareId(shareId);
        setCreatedAccessSummary({
          accessMode: response.data.shareLink.accessMode ?? accessMode,
          publicAccessType:
            response.data.shareLink.publicAccessType ??
            (accessMode === 'public' ? publicAccessType : undefined),
          maxViews:
            response.data.shareLink.maxViews ??
            (needsLimit ? maxViews : null),
          hasPassword: Boolean(
            response.data.shareLink.hasPassword ?? needsPassword
          ),
        });
        
        enqueueSnackbar('Share link created successfully', { variant: 'success' });
        await fetchResourceShares();
      } else {
        setError(response.data.error || 'Failed to create share link');
        enqueueSnackbar('Failed to create share link', { variant: 'error' });
      }
    } catch (error) {
      setError('An error occurred while creating the share link');
      enqueueSnackbar('Failed to create share link', { variant: 'error' });

    } finally {
      setLoading(false);
    }
  };
  
  // Handle sharing with users
  const handleShareWithUsers = async () => {
    if (selectedUserIds.length === 0) {
      enqueueSnackbar('Please select at least one person', { variant: 'warning' });
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      const resultAction = await dispatch(shareWithUsers({
        resourceType: itemType,
        resourceId: itemId,
        userIds: selectedUserIds,
        shareType: shareType
      }) as any);
      
      if (resultAction.meta.requestStatus === 'fulfilled') {
        enqueueSnackbar(`Successfully shared with ${selectedUserIds.length} users`, { variant: 'success' });
        setSelectedUserIds([]);
        await fetchResourceShares();
      } else {
        setError(resultAction.payload || 'Failed to share with users');
        enqueueSnackbar('Failed to share with users', { variant: 'error' });
      }
    } catch (error) {
      setError('An error occurred while sharing with users');
      enqueueSnackbar('Failed to share with users', { variant: 'error' });

    } finally {
      setLoading(false);
    }
  };
  
  // Copy link to clipboard
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
    enqueueSnackbar('Link copied to clipboard', { variant: 'info' });
  };
  
  // Remove a share
  const handleRemoveShare = async (shareId: string) => {
    try {
      const resultAction = await dispatch(removeShare(shareId) as any);
      
      if (resultAction.meta.requestStatus === 'fulfilled') {
        enqueueSnackbar('Share removed successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to remove share', { variant: 'error' });
      }
    } catch (error) {

      enqueueSnackbar('Failed to remove share', { variant: 'error' });
    }
  };

  // Update the share component integration with password protection
  const handleSetPassword = async (password: string) => {
    try {
      setLoading(true);
      
      // Get the share ID from the recently created share
      const shareId = selectedShareId;
      
      if (!shareId) {
        enqueueSnackbar('No share selected', { variant: 'error' });
        return;
      }
      
      // Call API to update the share with the password
      await api.put(`/shares/${shareId}/password`, {
        password
      });
      
      enqueueSnackbar('Password set successfully', { variant: 'success' });
    } catch (error) {

      enqueueSnackbar('Failed to set password', { variant: 'error' });
    } finally {
      setLoading(false);
      setPasswordDialogOpen(false);
    }
  };

  // Add reset password functionality
  const handleResetPassword = (shareId: string) => {
    setSelectedShareId(shareId);
    setPasswordDialogOpen(true);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      aria-labelledby="share-dialog-title"
    >
      <DialogTitle id="share-dialog-title">
        <Box display="flex" alignItems="center">
          <ShareIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            Share {itemType}: {itemName}
          </Typography>
        </Box>
      </DialogTitle>
      
      <Tabs value={tabValue} onChange={handleTabChange} aria-label="share options tabs" centered variant="scrollable">
        <Tab icon={<LinkIcon />} label="Get Link" />
        <Tab icon={<PersonAddIcon />} label="Share with Users" />
        <Tab icon={<ManageAccountsIcon />} label="Manage access" />
        <Tab icon={<TimeIcon />} label="Analytics" />
      </Tabs>
      
      <DialogContent dividers>
        {sharesError && <Alert severity="warning" action={<Button onClick={() => void fetchResourceShares()}>Retry</Button>}>{sharesError}</Alert>}
        <TabPanel value={tabValue} index={0}>
          {!linkCreated ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Create a link to share this {itemType}
              </Typography>
              
              <Alert severity="info">Share links provide view access. Use sharing with specific people to grant editing access.</Alert>
              
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={expirationEnabled}
                      onChange={(e) => setExpirationEnabled(e.target.checked)}
                    />
                  }
                  label="Set expiration"
                />
                
                {expirationEnabled && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Expiration date"
                        value={expirationDate}
                        onChange={(newDate) => setExpirationDate(newDate)}
                        slotProps={{ 
                          textField: { 
                            fullWidth: true,
                            variant: 'outlined',
                            InputProps: {
                              'aria-label': 'Set expiration date',
                            }
                          }
                        }}
                        minDate={new Date()}
                        disablePast={false}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TimePicker
                        label="Expiration time"
                        value={expirationTime}
                        onChange={(newTime) => setExpirationTime(newTime)}
                        slotProps={{ 
                          textField: { 
                            fullWidth: true,
                            variant: 'outlined',
                            helperText: "Time is in your local timezone",
                            InputProps: {
                              'aria-label': 'Set expiration time',
                            }
                          }
                        }}
                        ampm={true}
                        views={['hours', 'minutes']}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        The share will expire at exactly this time in your local timezone.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mt: 1 }} icon={<EventIcon />}>
                        The link will expire on {expirationDate && format(expirationDate, 'PP')} at {expirationTime && format(expirationTime, 'p')}
                      </Alert>
                    </Grid>
                  </Grid>
                )}
              </Box>
              
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel id="access-mode-label">Link access</InputLabel>
                <Select
                  labelId="access-mode-label"
                  value={accessMode}
                  label="Link access"
                  onChange={(e) => setAccessMode(e.target.value as ShareAccessMode)}
                >
                  <MenuItem value="public">Public — anyone with the link</MenuItem>
                  <MenuItem value="private">Private — organization members (must sign in)</MenuItem>
                </Select>
              </FormControl>

              {accessMode === 'public' && (
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel id="public-access-type-label">Public access type</InputLabel>
                  <Select
                    labelId="public-access-type-label"
                    value={publicAccessType}
                    label="Public access type"
                    onChange={(e) => setPublicAccessType(e.target.value as PublicAccessType)}
                  >
                    <MenuItem value="direct">Direct open (no password)</MenuItem>
                    <MenuItem value="password">Password protected</MenuItem>
                    <MenuItem value="limited_views">Limited views</MenuItem>
                    <MenuItem value="password_limited">Password + limited views</MenuItem>
                  </Select>
                </FormControl>
              )}

              {accessMode === 'public' &&
                (publicAccessType === 'limited_views' || publicAccessType === 'password_limited') && (
                <TextField
                  fullWidth
                  margin="normal"
                  type="number"
                  label="Maximum views"
                  inputProps={{ min: 1 }}
                  value={maxViews}
                  onChange={(e) => setMaxViews(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  helperText="Link stops working after this many successful opens"
                />
              )}

              {accessMode === 'public' &&
                (publicAccessType === 'password' || publicAccessType === 'password_limited') && (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    margin="dense"
                    type="password"
                    label="Link password"
                    value={linkPassword}
                    onChange={(e) => setLinkPassword(e.target.value)}
                  />
                  <TextField
                    fullWidth
                    margin="dense"
                    type="password"
                    label="Confirm password"
                    value={linkPasswordConfirm}
                    onChange={(e) => setLinkPasswordConfirm(e.target.value)}
                  />
                </Box>
              )}

              {accessMode === 'private' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Recipients must sign in with an account in your organization. The link alone is not enough.
                </Alert>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateLink}
                  disabled={loading}
                  startIcon={<LinkIcon />}
                  fullWidth
                >
                  {loading ? 'Creating...' : 'Create Share Link'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Your share link is ready
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={shareLink}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Copy link">
                          <IconButton 
                            edge="end" 
                            onClick={handleCopyLink}
                            color={copySuccess ? "success" : "default"}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  {createdAccessSummary?.accessMode === 'private' && (
                    <Chip label="Private (org login)" color="warning" variant="outlined" />
                  )}
                  {createdAccessSummary?.accessMode === 'public' && (
                    <Chip label="Public" color="success" variant="outlined" />
                  )}
                  {createdAccessSummary?.hasPassword && (
                    <Chip 
                      icon={<LockIcon />} 
                      label="Password Protected" 
                      color="primary" 
                      variant="outlined" 
                    />
                  )}
                  {createdAccessSummary?.maxViews != null && createdAccessSummary.maxViews > 0 && (
                    <Chip 
                      label={`Max ${createdAccessSummary.maxViews} views`} 
                      color="secondary" 
                      variant="outlined" 
                    />
                  )}
                  
                  {expirationEnabled && expirationDate && (
                    <Chip 
                      icon={<EventIcon />} 
                      label={`Expires: ${format(expirationDate, 'MMM d, yyyy')}`} 
                      color="secondary" 
                      variant="outlined" 
                    />
                  )}
                  
                  <Chip 
                    label="Permission: View" 
                    color="info" 
                    variant="outlined" 
                  />
                </Box>
              </Paper>
              
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setLinkCreated(false);
                    setShareLink('');
                    setAccessMode('public');
                    setPublicAccessType('direct');
                    setLinkPassword('');
                    setLinkPasswordConfirm('');
                    setCreatedAccessSummary(null);
                  }}
                  fullWidth
                >
                  Create Another Link
                </Button>
              </Box>
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Share with specific people
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              {SHARE_COLLABORATOR_HINT}
            </Alert>
            
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel id="share-permission-label">Permission</InputLabel>
              <Select
                labelId="share-permission-label"
                value={shareType}
                onChange={(e) => setShareType(e.target.value as ShareType)}
                label="Permission"
              >
                <MenuItem value="view">View</MenuItem>
                <MenuItem value="edit">Edit</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
              Select people in your organization
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Filter by name or email"
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              sx={{ mb: 1 }}
            />

            {usersError && <Alert severity="error" action={<Button onClick={() => void fetchUsers(userSearchQuery.trim())}>Retry</Button>}>{usersError}</Alert>}
            {nextUserOffset !== null && <Button disabled={loadingUsers} onClick={() => void fetchUsers(userSearchQuery.trim(), nextUserOffset)}>Load more people</Button>}
            <Paper variant="outlined" sx={{ maxHeight: 280, overflow: 'auto' }}>
              {loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : users.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No one in your organization has opened Drive yet.
                </Typography>
              ) : filteredUsers.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No users match your search.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {filteredUsers.map((user) => {
                    const checked = selectedUserIds.includes(user.id);
                    return (
                      <ListItem key={user.id} disablePadding divider>
                        <ListItemButton
                          onClick={() => toggleUserSelection(user.id)}
                          dense
                        >
                          <Checkbox
                            edge="start"
                            checked={checked}
                            tabIndex={-1}
                            disableRipple
                          />
                          <ListItemText primary={user.name} secondary={user.email} />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Paper>

            {selectedUserIds.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedUserIds.length} selected
              </Typography>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleShareWithUsers}
                disabled={loading || selectedUserIds.length === 0}
                startIcon={<PersonAddIcon />}
                fullWidth
              >
                {loading
                  ? 'Sharing...'
                  : selectedUserIds.length > 0
                    ? `Share with ${selectedUserIds.length} ${selectedUserIds.length === 1 ? 'person' : 'people'}`
                    : 'Share with selected people'}
              </Button>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Active shares for this {itemType}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Revoke link shares or remove people who have direct access.
            </Typography>

            {loadingShares ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : shareLinksList.length === 0 && userSharesList.length === 0 ? (
              <Alert severity="info">
                No active shares yet. Create a link or share with users in the other tabs.
              </Alert>
            ) : (
              <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
                {shareLinksList.length > 0 && (
                  <>
                    <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                      <Typography variant="subtitle2">Link shares</Typography>
                    </Box>
                    <List dense disablePadding>
                      {shareLinksList.map((link) => (
                        <ListItem key={link.id} divider>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <LinkIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={formatLinkShareLabel(link)}
                            secondary={`${link.accessCount} view${link.accessCount === 1 ? '' : 's'}`}
                          />
                          <ListItemSecondaryAction>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              startIcon={<LinkOffIcon />}
                              disabled={revokingId === link.id}
                              onClick={() => handleRevokeLink(link.id)}
                            >
                              Revoke
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}

                {shareLinksList.length > 0 && userSharesList.length > 0 && <Divider />}

                {userSharesList.length > 0 && (
                  <>
                    <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                      <Typography variant="subtitle2">People with access</Typography>
                    </Box>
                    <List dense disablePadding>
                      {userSharesList.map((entry) => (
                        <ListItem key={entry.id} divider>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <PersonAddIcon fontSize="small" color="secondary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={entry.userName}
                            secondary={`${entry.userEmail} · ${entry.shareType} access${entry.inherited ? ' · inherited' : entry.managedByAccessOps ? ' · managed in AccessOps' : ''}`}
                          />
                          <ListItemSecondaryAction>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              startIcon={<PersonRemoveIcon />}
                              disabled={revokingId === entry.id || !entry.removable}
                              onClick={() => handleRevokeUserAccess(entry.id)}
                            >
                              Remove
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </Paper>
            )}
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          {shareLinksList.length > 0 ? (
            <Box>
              {shareLinksList.length > 1 && (
                <Box mb={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Select Share</InputLabel>
                    <Select
                      value={selectedShareId || shareLinksList[0].id}
                      onChange={(e) => setSelectedShareId(e.target.value as string)}
                      label="Select Share"
                    >
                      {shareLinksList.map((share) => (
                        <MenuItem key={share.id} value={share.id}>
                          {share.shareType} link · {new Date(share.createdAt).toLocaleDateString()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              
              <Box mb={3} display="flex" flexWrap="wrap" gap={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<LockIcon />}
                  onClick={() => handleResetPassword(selectedShareId || shareLinksList[0].id)}
                >
                  {shareLinksList.find(s => s.id === (selectedShareId || shareLinksList[0].id))?.hasPassword 
                    ? 'Reset Password' 
                    : 'Add Password'}
                </Button>
              </Box>
              
              {selectedShareId && <ShareAnalytics shareId={selectedShareId} />}
            </Box>
          ) : (
            <Box textAlign="center" py={3}>
              <Typography variant="body1" color="textSecondary">
                No link shares found for this {itemType}. Create a share first to view analytics.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => setTabValue(0)} 
                startIcon={<LinkIcon />}
                sx={{ mt: 2 }}
              >
                Create a share link
              </Button>
            </Box>
          )}
        </TabPanel>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
      
      {/* Password dialog */}
      <SetPasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        onSetPassword={handleSetPassword}
        isReset={selectedShareId !== null}
      />
      
      {/* Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        message="Link copied to clipboard"
      />
    </Dialog>
  );
};

export default ShareDialog; 